import React, {
  useEffect,
  useState,
} from "react";

import {
  FiArrowUpRight,
  FiEye,
  FiX,
  FiLayers,
  FiShield,
  FiAlertTriangle,
  FiGitBranch,
  FiInfo,
  FiDownload,
  FiPaperclip,
} from "react-icons/fi";

import { motion, AnimatePresence } from "framer-motion";

import { GetRequirements,requirementService } from "../../services/ApiService";
import { UpdateRequirement } from "../../services/ApiService";

import "./Dashboard.css";

const Dashboard = () => {
  const [requirements, setRequirements] =useState([]);
  const [loading, setLoading] =useState(true);
  const [selectedRequirement, setSelectedRequirement] =useState(null);
  const [isEditing, setIsEditing] =useState(false);
  const [editData, setEditData] =useState(null);
  const [selectedVersion, setSelectedVersion] =useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  // Add these near your other useState hooks in Dashboard.js
const [versionRemark, setVersionRemark] = useState("");
const [uploadingDocs, setUploadingDocs] = useState([]); // Array format: [{ name: '', file: Blob }]

  // ===================================
  // FETCH REQUIREMENTS
  // ===================================

  useEffect(() => {fetchRequirements();}, []);

const getVersionChanges = (versionNo) => {

  return editData.change_logs?.filter(
    (log) => log.version === versionNo
  ) || [];
};

<motion.div className="version-popup" 
initial={{
    opacity: 0,
    scale: 0.9,
    x: "-50%",
    y: "-50%",
  }}

  animate={{
    opacity: 1,
    scale: 1,
    x: "-50%",
    y: "-50%",
  }}

  exit={{
    opacity: 0,
    scale: 0.9,
    x: "-50%",
    y: "-50%",
  }}
></motion.div>

// ===================================
// FEATURE FIELD HIGHLIGHT
// ===================================
const getFeatureVersionChanges = (versionNo) => {

  return (
    editData.feature_change_logs?.filter(
      (log) =>
        log.version === versionNo
    ) || []
  );
};


const featureChanged = (version,field,value) => {

  return getFeatureVersionChanges(version).find(
    (item) =>
      item.field_name === field &&
      item.new_value === value
  );
};





  // ========================================================
  // STEP 1: Fetch all projects on initial mounting load
  // ========================================================
  useEffect(() => {
    const fetchInitialProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await requirementService.getProjects();
        
        // Handle both wrapped { status: true, data: [...] } and raw array responses safely
        const projectList = response.data || response || [];
        setProjects(projectList);

        // Auto-select the first project track if available to kickstart the UI
        if (projectList.length > 0) {
          setSelectedProjectId(projectList[0].id); // verify if your field is 'id' or '_id'
        }
      } catch (error) {
        console.error("Failed to load initial projects array:", error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchInitialProjects();
  }, []);

  // ========================================================
  // STEP 2: Fetch project-wise requirements
  // ========================================================
  const fetchRequirements = async () => {
    // Guard clause: If no project id is actively selected, clear items grid and exit
    if (!selectedProjectId) {
      setRequirements([]);
      return;
    }

    setLoading(true);
    try {
      // Pass the selected id state down to the API service layer
      const response = await GetRequirements.getRequirements(selectedProjectId);

      if (response?.status) {
        setRequirements(response.data || []);
      } else {
        setRequirements([]);
      }
    } catch (error) {
      console.error("Failed to load project requirements:", error);
      setRequirements([]);
    } finally {
      setLoading(false);
    }
  };

  // Automatically trigger api sync tracking every time selectedProjectId updates
  useEffect(() => {
    fetchRequirements();
  }, [selectedProjectId]);


  // const fetchRequirements =
  //   async () => {
  //     setLoading(true);

  //     try {
  //       const response =
  //         await GetRequirements.getRequirements();

  //       if (response?.status) {
  //         setRequirements(
  //           response.data || []
  //         );
  //       }
  //     } catch (error) {
  //       console.error(error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  // ===================================
  // COUNTS
  // ===================================

  const mustHaveCount =
    requirements.filter(
      (item) =>
        item.requirement_moscow ===
        "Must Have"
    ).length;

  const highPriorityCount =
    requirements.filter(
      (item) =>
        item.priority === "High"
    ).length;

  // ===================================
  // HELPERS
  // ===================================

  const priorityClass = (
    priority
  ) => {
    switch (priority) {
      case "High":
        return "high";
      case "Medium":
        return "medium";
      case "Low":
        return "low";
      default:
        return "";
    }
  };

const handleUpdate =
  async () => {
    try {
      const formData =
        new FormData();

      formData.append(
        "updated_by",
        localStorage.getItem(
          "user_id"
        )
      );

      formData.append(
        "version_remark",
        editData.version_remark ||
          ""
      );

      formData.append(
        "requirement_title_reference",
        editData.requirement_title_reference ||
          ""
      );

      formData.append(
        "requirement_purpose",
        editData.requirement_purpose ||
          ""
      );

      formData.append(
        "requirement_description",
        editData.requirement_description ||
          ""
      );

      formData.append(
        "requirement_moscow",
        editData.requirement_moscow ||
          ""
      );

      formData.append(
        "priority",
        editData.priority ||
          ""
      );

      formData.append(
        "reference",
        editData.reference ||
          ""
      );

      formData.append(
        "technical_issues",
        editData.technical_issues ||
          ""
      );

      formData.append(
        "risk",
        editData.risk ||
          ""
      );

      formData.append(
        "high_level_dependencies",
        editData.high_level_dependencies ||
          ""
      );

      formData.append(
        "acceptance_criteria",
        editData.acceptance_criteria ||
          ""
      );

      formData.append(
        "verification_method",
        editData.verification_method ||
          ""
      );

      formData.append(
        "features",
        JSON.stringify(
          editData.features ||
            []
        )
      );

      // DOCUMENTS
      editData.new_documents?.forEach(
        (doc) => {
          formData.append(
            "document_names[]",
            doc.name
          );

          formData.append(
            "document_files[]",
            doc.file
          );
        }
      );

      const response =
        await UpdateRequirement.updateRequirement(
          editData.id,
          formData
        );

      const updated =
        response.data;

      setRequirements(
        (prev) =>
          prev.map((req) =>
            req.id ===
            updated.id
              ? updated
              : req
          )
      );

      setEditData({
        ...updated,
        new_documents: [],
      });

      setIsEditing(false);
    } catch (error) {
      console.error(
        error
      );
      alert(
        "Failed to update requirement"
      );
    }
  };

//   const handleUpdate = async () => {

//   try {

//     const user = JSON.parse(
//       localStorage.getItem("user")
//     );

//     const payload = {

//       updated_by: user.user_id,

//       requirement_title_reference:
//         editData.requirement_title_reference,

//       requirement_purpose:
//         editData.requirement_purpose,

//       requirement_description:
//         editData.requirement_description,

//       requirement_moscow:
//         editData.requirement_moscow,

//       priority:
//         editData.priority,

//       reference:
//         editData.reference,

//       technical_issues:
//         editData.technical_issues,

//       risk:
//         editData.risk,

//       high_level_dependencies:
//         editData.high_level_dependencies,

//       acceptance_criteria:
//         editData.acceptance_criteria,

//       verification_method:
//         editData.verification_method,

//       features:
//         editData.features,
//     };

//     const response =
//       await UpdateRequirement.updateRequirement(
//         editData.id,
//         payload
//       );

//     alert(response.message);
//      // IMPORTANT
//     setSelectedRequirement(response.data);
//     setEditData(response.data);

//     setIsEditing(false);

//     fetchRequirements();

//   } catch (error) {

//     console.error(error);

//     alert("Update Failed");
//   }
// };




  return (
    <div className="dashboard-page">
      {/* HEADER */}
      <div className="dashboard-header">
        <div>
          <h1>
            Requirements Dashboard
          </h1>

          <p>Manage requirement modules, compliance,versions & risks</p>
        </div>

      {/* ========================================================= */}
        {/* ADD THE PROJECT DROPDOWN SEGMENT HERE                    */}
        {/* ========================================================= */}
        <div className="project-select-filter" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <label style={{ fontSize: "12px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>
            Select Active Project Track
          </label>
          <select 
            value={selectedProjectId} 
            onChange={(e) => setSelectedProjectId(e.target.value)}
            disabled={loadingProjects}
            style={{ 
              padding: "10px 16px", 
              borderRadius: "8px", 
              border: "1px solid #cbd5e1", 
              fontSize: "14px", 
              fontWeight: "600",
              color: "#1e293b",
              background: "#ffffff",
              minWidth: "240px",
              cursor: "pointer"
            }}
          >
            {loadingProjects && <option value="">Loading tracking projects...</option>}
            {!loadingProjects && projects.length === 0 && <option value="">No projects assigned</option>}
            {projects.map((proj) => (
              <option key={proj.id} value={proj.id}>
                {proj.project_title || proj.name}
              </option>
            ))}
          </select>
        </div>
        {/* ========================================================= */}

      </div>

      {/* STATS */}
      <div className="stats-grid">
        <div className="stat-card">
          <div>
            <h3>{requirements.length}</h3>
            <p>Total Requirements</p>
          </div>

          <FiArrowUpRight />
        </div>

        <div className="stat-card">
          <div>
            <h3>
              {mustHaveCount}
            </h3>
            <p>Must Have</p>
          </div>

          <FiShield />
        </div>

        <div className="stat-card">
          <div>
            <h3>
              {
                highPriorityCount
              }
            </h3>
            <p>
              High Priority
            </p>
          </div>

          <FiAlertTriangle />
        </div>

        <div className="stat-card">
          <div>
            <h3>
              {
                requirements.reduce(
                  (
                    acc,
                    item
                  ) =>
                    acc +
                    item
                      .features
                      ?.length,
                  0
                )
              }
            </h3>

            <p>
              Total Features
            </p>
          </div>

          <FiLayers />
        </div>
      </div>

      {/* CONTENT */}
      {loading ? (
        <div className="loading-state">
          Loading
          requirements...
        </div>
      ) : requirements.length ===
        0 ? (
        <div className="empty-state">
          No requirements
          found
        </div>
      ) : (
        <div className="requirements-grid">
          {requirements.map(
            (item) => (
              <motion.div
                key={item.id}
                className="requirement-card"
                whileHover={{
                  y: -4,
                }}
              >
                {/* TOP */}
                <div className="card-top">
                  <span className="req-id">
                    {
                      item.requirement_id
                    }
                  </span>

                  <span className="version-chip">
                    v
                    {
                      item.current_version
                    }
                  </span>
                </div>

                {/* TITLE */}
                <h3>
                  {
                    item.requirement_title_reference
                  }
                </h3>

                <p>
                  {
                    item.requirement_purpose
                  }
                </p>

                {/* CHIPS */}
                <div className="card-tags">
                  <span
                    className={`priority-chip ${priorityClass(
                      item.priority
                    )}`}
                  >
                    {
                      item.priority
                    }
                  </span>

                  <span className="moscow-chip">
                    {
                      item.requirement_moscow
                    }
                  </span>
                </div>

                {/* FOOTER */}
                <div className="card-footer">
                  <span>
                    {
                      item
                        .features
                        ?.length
                    }{" "}
                    Features
                  </span>

                  <button
                    className="view-btn"
                    onClick={() => {
  setEditData(item);
  // setEditData(item);
}}
                  >
                    <FiEye />
                    View Details
                  </button>
                </div>
              </motion.div>
            )
          )}
        </div>
      )}

      {/* DRAWER */}
      <AnimatePresence>
        {editData && (
          <>
            <motion.div
              className="drawer-overlay"
              onClick={() =>
                setEditData(
                  null
                )
              }
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            />

            <motion.div
              className="drawer"
              initial={{
                x: "100%",
              }}
              animate={{
                x: 0,
              }}
              exit={{
                x: "100%",
              }}
              transition={{
                duration: 0.3,
              }}
            >
              {/* HEADER */}
              <div className="drawer-header">
                <div>
                 {isEditing ? (

  <input
    type="text"
    value={
      editData.requirement_title_reference
    }
    onChange={(e) =>
      setEditData({
        ...editData,
        requirement_title_reference:
          e.target.value,
      })
    }
  />

) : (

  <h2>
    {
      editData.requirement_title_reference
    }
  </h2>

)}

                  <p>
                    {
                      editData.requirement_id
                    }
                  </p>
                </div>

                <div className="drawer-actions">
                    <button
    className="close-btn"
    onClick={() => {
      setEditData(null);
      setIsEditing(false);
    }}
  >
    <FiX />
  </button>

  {!isEditing ? (

    <button
      className="edit-btn"
      onClick={() =>
        setIsEditing(true)
      }
    >
      Edit
    </button>

  ) : (

    <button
      className="save-btn"
      onClick={handleUpdate}
    >
      Save
    </button>

  )}



</div>

                {/* <button
                  className="close-btn"
                  onClick={() =>
                    seteditData(
                      null
                    )
                  }
                >
                  <FiX />
                </button> */}
              </div>

              {/* OVERVIEW */}
              <div className="drawer-section">
                <h4>
                  Overview
                </h4>

                <div className="detail-grid">
                  <div>
                    <span>
                      Purpose
                    </span>
                   {isEditing ? (

  <textarea
    value={
      editData.requirement_purpose
    }
    onChange={(e) =>
      setEditData({
        ...editData,
        requirement_purpose:
          e.target.value,
      })
    }
  />

) : (

  <p>
    {
      editData.requirement_purpose
    }
  </p>

)}
                  </div>

                  <div>
                    <span>
                      Priority
                    </span>
                   {isEditing ? (

  <select
    value={editData.priority}
    onChange={(e) =>
      setEditData({
        ...editData,
        priority: e.target.value,
      })
    }
  >
    <option value="High">High</option>
    <option value="Medium">Medium</option>
    <option value="Low">Low</option>
  </select>

) : (

  <p>
    {
      editData.priority
    }
  </p>

)}
                  </div>

                  <div>
                    <span>
                      MoSCoW
                    </span>
                   {isEditing ? (

  <select
    value={
      editData.requirement_moscow
    }
    onChange={(e) =>
      setEditData({
        ...editData,
        requirement_moscow:
          e.target.value,
      })
    }
  >
    <option value="Must Have">
      Must Have
    </option>

    <option value="Should Have">
      Should Have
    </option>

    <option value="Could Have">
      Could Have
    </option>

    <option value="Won't Have">
      Won't Have
    </option>
  </select>

) : (

  <p>
    {
      editData.requirement_moscow
    }
  </p>

)}
                  </div>

                  <div>
                    <span>
                      Version
                    </span>
                    <p>
                      {
                        editData.current_version
                      }
                    </p>
                  </div>
                </div>

                <div className="description-box">
                  <span>
                    Description
                  </span>

                 {isEditing ? (

  <textarea
    value={
      editData.requirement_description
    }
    onChange={(e) =>
      setEditData({
        ...editData,
        requirement_description:
          e.target.value,
      })
    }
  />

) : (

  <p>
    {
      editData.requirement_description
    }
  </p>

)}
                </div>
              </div>
<div className="description-box">
  <span>Reference</span>

  {isEditing ? (

    <textarea
      value={editData.reference || ""}
      onChange={(e) =>
        setEditData({
          ...editData,
          reference: e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.reference
      }
    </p>

  )}
</div>

<div className="description-box">
  <span>Technical Issues</span>

  {isEditing ? (

    <textarea
      value={
        editData.technical_issues || ""
      }
      onChange={(e) =>
        setEditData({
          ...editData,
          technical_issues:
            e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.technical_issues
      }
    </p>

  )}
</div>

<div className="description-box">
  <span>Risk</span>

  {isEditing ? (

    <textarea
      value={editData.risk || ""}
      onChange={(e) =>
        setEditData({
          ...editData,
          risk: e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.risk
      }
    </p>

  )}
</div>

<div className="description-box">
  <span>High-Level Dependencies</span>

  {isEditing ? (

    <textarea
      value={
        editData.high_level_dependencies || ""
      }
      onChange={(e) =>
        setEditData({
          ...editData,
          high_level_dependencies:
            e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.high_level_dependencies
      }
    </p>

  )}
</div>

<div className="description-box">
  <span>Acceptance Criteria</span>

  {isEditing ? (

    <textarea
      value={
        editData.acceptance_criteria || ""
      }
      onChange={(e) =>
        setEditData({
          ...editData,
          acceptance_criteria:
            e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.acceptance_criteria
      }
    </p>

  )}
</div>

<div className="description-box">
  <span>Verification Method</span>

  {isEditing ? (

    <textarea
      value={
        editData.verification_method || ""
      }
      onChange={(e) =>
        setEditData({
          ...editData,
          verification_method:
            e.target.value,
        })
      }
    />

  ) : (

    <p>
      {
        editData.verification_method
      }
    </p>

  )}
</div>
              {/* FEATURES */}
              <div className="drawer-section">
                <h4>
                  Features
                </h4>

                <div className="feature-table-wrapper">
  <table>
    <thead>
      <tr>
        <th>Title</th>
        <th>Description</th>
        <th>Feature</th>
        <th>MoSCoW</th>
        <th>Risk</th>
        <th>Dependencies</th>
        <th>Priority</th>
        <th>Standard Mapping</th>
        <th>Target Achievement</th>
      </tr>
    </thead>

    <tbody>
      {editData.features?.map(
        (feature, index) => (
          <tr key={feature.id}>

            {/* TITLE */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.title || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].title =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.title
              )}
            </td>

            {/* DESCRIPTION */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.description || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].description =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.description
              )}
            </td>

            {/* FEATURE */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.feature || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].feature =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.feature
              )}
            </td>

            {/* MOSCOW */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.moscow || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].moscow =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.moscow
              )}
            </td>

            {/* RISK */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.risk || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].risk =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.risk
              )}
            </td>

            {/* DEPENDENCIES */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.dependencies || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].dependencies =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.dependencies
              )}
            </td>

            {/* PRIORITY */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.priority || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].priority =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.priority
              )}
            </td>

            {/* STANDARD MAPPING */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.self_standard_mapping || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].self_standard_mapping =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.self_standard_mapping
              )}
            </td>

            {/* TARGET ACHIEVEMENT */}
            <td>
              {isEditing ? (
                <input
                  type="text"
                  value={feature.target_standard_achievement || ""}
                  onChange={(e) => {
                    const updatedFeatures = [...editData.features];

                    updatedFeatures[index].target_standard_achievement =
                      e.target.value;

                    setEditData({
                      ...editData,
                      features: updatedFeatures,
                    });
                  }}
                />
              ) : (
                feature.target_standard_achievement
              )}
            </td>

          </tr>
        )
      )}
    </tbody>
  </table>
</div>
              </div>
{/* ========================================================= */}
              {/* REMARK & UPLOADED DOCUMENTS SECTIONS (NEW DIRECT FIELDS)   */}
              {/* ========================================================= */}
              
              {/* VERSION REMARK */}
              <div className="drawer-section">
                <h4>Version Remark</h4>
                <div className="description-box">
                  {isEditing ? (
                    <textarea
                      placeholder="Add a remark for this version configuration..."
                      value={editData.version_remark || ""}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          version_remark: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <p className={!editData.version_remark ? "text-muted" : ""}>
                      {editData.version_remark || "No remarks provided for this version."}
                    </p>
                  )
                }
                </div>
              </div>

{/* ATTACHED DOCUMENTS */}
<div className="drawer-section">
  <h4>Uploaded Documents</h4>

  {/* Existing Documents */}
  {editData.uploaded_documents_details?.length > 0 ? (
    <div className="documents-grid-layout">
      {editData.uploaded_documents_details.map(
        (doc, idx) => (
          <div
            key={idx}
            className="document-download-card"
          >
            <div className="doc-icon-wrapper">
              <FiPaperclip />
            </div>

            <div className="doc-meta-info">
              <span
                className="doc-title"
                title={doc.document_name}
              >
                {doc.document_name}
              </span>

              <span className="doc-sub">
                Version:{" "}
                {doc.version ||
                  editData.current_version}
              </span>
            </div>

            <a
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="download-action-btn"
              download={doc.document_name}
            >
              <FiDownload />
            </a>
          </div>
        )
      )}
    </div>
  ) : (
    <div className="empty-state-subtle">
      <p>
        No documents attached to
        this requirement.
      </p>
    </div>
  )}

  {/* ADD NEW DOCUMENTS */}
  <div
    style={{
      marginTop: "16px",
    }}
  >
    <label
      style={{
        fontWeight: "600",
        marginBottom: "8px",
        display: "block",
      }}
    >
      Add Documents
    </label>

    <input
      type="file"
      multiple
      disabled={!isEditing}
      onChange={(e) => {
        const files =
          Array.from(
            e.target.files
          );

        const formattedFiles =
          files.map(
            (file) => ({
              file,
              name:
                file.name.split(
                  "."
                )[0],
            })
          );

        setEditData(
          (prev) => ({
            ...prev,
            new_documents: [
              ...(prev.new_documents ||
                []),
              ...formattedFiles,
            ],
          })
        );
      }}
    />
  </div>

  {/* NEW DOCUMENT PREVIEW */}
  {editData.new_documents
    ?.length > 0 && (
    <div
      className="documents-grid-layout"
      style={{
        marginTop: "16px",
      }}
    >
      {editData.new_documents.map(
        (doc, idx) => (
          <div
            key={idx}
            className="document-download-card"
          >
            <div className="doc-icon-wrapper">
              <FiPaperclip />
            </div>

            <div className="doc-meta-info">
              <input
                type="text"
                value={doc.name}
                disabled={!isEditing}
                placeholder="Document Name"
                onChange={(e) => {
                  const updatedDocs =
                    [
                      ...editData.new_documents,
                    ];

                  updatedDocs[
                    idx
                  ].name =
                    e.target.value;

                  setEditData({
                    ...editData,
                    new_documents:
                      updatedDocs,
                  });
                }}
              />

              <span className="doc-sub">
                {doc.file.name}
              </span>
            </div>

            <button
              className="close-btn"
              onClick={() => {
                const updatedDocs =
                  editData.new_documents.filter(
                    (
                      _,
                      i
                    ) =>
                      i !== idx
                  );

                setEditData({
                  ...editData,
                  new_documents:
                    updatedDocs,
                });
              }}
            >
              <FiX />
            </button>
          </div>
        )
      )}
    </div>
  )}
</div>

              {/* VERSION HISTORY */}
              {/* VERSION HISTORY */}
<div className="drawer-section">
  <h4>
    Version History
  </h4>

  <div className="version-list">

    {editData.versions?.map(
      (version) => (

        <div
          key={version.id}
          className="version-item"
        >
          <div className="version-left">
            <FiGitBranch size={20} />

            <div>
              <strong>
                v{version.version}
              </strong>

              <p>
                {
                  version.requirement_title_reference
                }
              </p>
            </div>
          </div>

          <button
            className="version-view-btn"
            onClick={() =>
              setSelectedVersion(version)
            }
          >
            <FiEye />
            View Details
          </button>
        </div>
      )
    )}
  </div>
</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {/* VERSION DETAILS POPUP */}

<AnimatePresence>
  {selectedVersion && (

    <>
      <motion.div
        className="drawer-overlay"
        onClick={() =>
          setSelectedVersion(null)
        }
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      <motion.div className="version-popup" initial={{opacity: 0,scale: 0.9,x: "-50%",y: "-50%",}} animate={{opacity: 1,scale: 1,x: "-50%",y: "-50%",}} exit={{opacity: 0,scale: 0.9,x: "-50%",y: "-50%",}}>
        {/* HEADER */}

        <div className="version-popup-header">

          <div>
            <h2>
              Version v
              {selectedVersion.version}
            </h2><br></br>

            <p>
              Updated By :
              {" "}
              {
                selectedVersion
                .updated_by
                ?.username
              }
            </p>

            <p> Email : {" "} {selectedVersion.updated_by?.email} </p> 
            <p> Updated Time : {" "}  {new Date(selectedVersion.created_at).toLocaleString()}</p>
          </div>

          <button className="close-btn" onClick={() => setSelectedVersion(null)}>
            <FiX />
          </button>
        </div>
{/* VERSION REMARK */}
<div className="change-log-section">
  <h3>
    Version Remark
  </h3>

  <div className="description-box">
    <p>
      {selectedVersion.version_remark ||
        "No remark added"}
    </p>
  </div>
</div>

{/* DOCUMENTS */}
<div className="change-log-section">
  <h3>
    Uploaded Documents
  </h3>

  {!selectedVersion
    ?.uploaded_documents_details
    ?.length ? (
    <p>
      No documents uploaded
    </p>
  ) : (
    <div className="documents-grid-layout">
      {selectedVersion.uploaded_documents_details.map(
        (doc, idx) => (
          <div
            key={idx}
            className="document-download-card"
          >
            <div className="doc-icon-wrapper">
              <FiPaperclip />
            </div>

            <div className="doc-meta-info">
              <span className="doc-title">
                {
                  doc.document_name
                }
              </span>

              <span className="doc-sub">
                Version:
                {doc.version}
              </span>
            </div>

            <a
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="download-action-btn"
            >
              <FiDownload />
            </a>
          </div>
        )
      )}
    </div>
  )}
</div>
        {/* TABLE */}

        <div className="version-table-wrapper">

          <table className="version-table">

            <thead>
              <tr>
                <th>Field</th>
                <th>Value</th>
              </tr>
            </thead>

            <tbody>

              {Object.entries({
                requirement_title_reference:
                  selectedVersion.requirement_title_reference,

                requirement_purpose:
                  selectedVersion.requirement_purpose,

                requirement_description:
                  selectedVersion.requirement_description,

                requirement_moscow:
                  selectedVersion.requirement_moscow,

                priority:
                  selectedVersion.priority,

                reference:
                  selectedVersion.reference,

                technical_issues:
                  selectedVersion.technical_issues,

                risk:
                  selectedVersion.risk,

                high_level_dependencies:
                  selectedVersion.high_level_dependencies,

                acceptance_criteria:
                  selectedVersion.acceptance_criteria,

                verification_method:
                  selectedVersion.verification_method,

              }).map(([key, value]) => {

                const changed =
                  getVersionChanges(
                    selectedVersion.version
                  ).find(
                    (item) =>
                      item.field_name === key
                  );

                return (
                  <tr
                    key={key}
                    className={
                      changed
                        ? "highlight-row"
                        : ""
                    }
                  >
                    <td>
                      {key}
                    </td>

                    <td>
                      {value || "-"}
                    </td>
                  </tr>
                );
              })}

            </tbody>
          </table>
        </div>

        {/* CHANGE LOGS */}

        <div className="change-log-section">

          <h3>
            Updated Fields
          </h3>

          {getVersionChanges(
            selectedVersion.version
          ).length === 0 ? (

            <p>
              No changes found
            </p>

          ) : (

            <table className="change-table">

              <thead>
                <tr>
                  <th>Field</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>

              <tbody>

                {getVersionChanges(
                  selectedVersion.version
                ).map((log) => (

                  <tr key={log.id}>
                    <td>
                      {log.field_name}
                    </td>

                    <td>
                      {log.old_value}
                    </td>

                    <td className="updated-value">
                      {log.new_value}
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          )}
        </div>

        {/* FEATURE HISTORY */}

        <div className="change-log-section">

          <h3>
            Feature Versions
          </h3>

         {/* <div className="feature-table-wrapper"> */}
  <table className="change-table">
    <thead>
      <tr>
        <th>Title</th>
        <th>Description</th>
        <th>Feature</th>
        <th>MoSCoW</th>
        <th>Risk</th>
        <th>Dependencies</th>
        <th>Priority</th>
        <th>Standard Mapping</th>
        <th>Target Achievement</th>
      </tr>
    </thead>

   <tbody>
  {editData.features?.map((feature) => (
    <tr key={feature.id}>

      {/* TITLE */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "title",
            feature.title
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.title}
      </td>

      {/* DESCRIPTION */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "description",
            feature.description
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.description}
      </td>

      {/* FEATURE */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "feature",
            feature.feature
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.feature}
      </td>

      {/* MOSCOW */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "moscow",
            feature.moscow
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.moscow}
      </td>

      {/* RISK */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "risk",
            feature.risk
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.risk}
      </td>

      {/* DEPENDENCIES */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "dependencies",
            feature.dependencies
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.dependencies}
      </td>

      {/* PRIORITY */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "priority",
            feature.priority
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.priority}
      </td>

      {/* STANDARD MAPPING */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "self_standard_mapping",
            feature.self_standard_mapping
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.self_standard_mapping}
      </td>

      {/* TARGET ACHIEVEMENT */}
      <td
        className={
          featureChanged(
            selectedVersion.version,
            "target_standard_achievement",
            feature.target_standard_achievement
          )
            ? "highlight-cell"
            : ""
        }
      >
        {feature.target_standard_achievement}
      </td>

    </tr>
  ))}
</tbody>
  </table>
{/* </div> */}
        </div>

      </motion.div>
    </>
  )}
</AnimatePresence>
    </div>
  );
};

export default Dashboard;






// import React, {
//   useEffect,
//   useState,
// } from "react";

// import {
//   FiArrowUpRight,
//   FiEye,
//   FiLayers,
//   FiShield,
//   FiAlertTriangle,
// } from "react-icons/fi";

// import { motion } from "framer-motion";

// import { GetRequirements } from "../../services/ApiService";

// import ViewDetails from "./ViewDetail";

// import "./Dashboard.css";

// const Dashboard = () => {
//   const [requirements, setRequirements] =
//     useState([]);

//   const [loading, setLoading] =
//     useState(true);

//   const [selectedRequirement, setSelectedRequirement] =
//     useState(null);

//   useEffect(() => {
//     fetchRequirements();
//   }, []);

//   const fetchRequirements =
//     async () => {
//       setLoading(true);

//       try {
//         const response =
//           await GetRequirements.getRequirements();

//         if (response?.status) {
//           setRequirements(
//             response.data || []
//           );
//         }
//       } catch (error) {
//         console.error(error);
//       } finally {
//         setLoading(false);
//       }
//     };

//   const priorityClass = (
//     priority
//   ) => {
//     switch (priority) {
//       case "High":
//         return "high";
//       case "Medium":
//         return "medium";
//       case "Low":
//         return "low";
//       default:
//         return "";
//     }
//   };

//   const mustHaveCount =
//     requirements.filter(
//       (item) =>
//         item.requirement_moscow ===
//         "Must Have"
//     ).length;

//   const highPriorityCount =
//     requirements.filter(
//       (item) =>
//         item.priority === "High"
//     ).length;

//   return (
//     <div className="dashboard-page">
//       {/* HEADER */}
//       <div className="dashboard-header">
//         <div>
//           <h1>
//             Requirements Dashboard
//           </h1>

//           <p>
//             Manage requirement
//             modules, compliance,
//             versions & risks
//           </p>
//         </div>
//       </div>

//       {/* STATS */}
//       <div className="stats-grid">
//         <div className="stat-card">
//           <div>
//             <h3>
//               {
//                 requirements.length
//               }
//             </h3>

//             <p>
//               Total Requirements
//             </p>
//           </div>

//           <FiArrowUpRight />
//         </div>

//         <div className="stat-card">
//           <div>
//             <h3>
//               {mustHaveCount}
//             </h3>

//             <p>Must Have</p>
//           </div>

//           <FiShield />
//         </div>

//         <div className="stat-card">
//           <div>
//             <h3>
//               {
//                 highPriorityCount
//               }
//             </h3>

//             <p>
//               High Priority
//             </p>
//           </div>

//           <FiAlertTriangle />
//         </div>

//         <div className="stat-card">
//           <div>
//             <h3>
//               {
//                 requirements.reduce(
//                   (
//                     acc,
//                     item
//                   ) =>
//                     acc +
//                     (item.features
//                       ?.length ||
//                       0),
//                   0
//                 )
//               }
//             </h3>

//             <p>
//               Total Features
//             </p>
//           </div>

//           <FiLayers />
//         </div>
//       </div>

//       {/* CONTENT */}
//       {loading ? (
//         <div className="loading-state">
//           Loading
//           requirements...
//         </div>
//       ) : requirements.length ===
//         0 ? (
//         <div className="empty-state">
//           No requirements
//           found
//         </div>
//       ) : (
//         <div className="requirements-grid">
//           {requirements.map(
//             (item) => (
//               <motion.div
//                 key={item.id}
//                 className="requirement-card"
//                 whileHover={{
//                   y: -4,
//                 }}
//               >
//                 {/* TOP */}
//                 <div className="card-top">
//                   <span className="req-id">
//                     {
//                       item.requirement_id
//                     }
//                   </span>

//                   <span className="version-chip">
//                     v
//                     {
//                       item.current_version
//                     }
//                   </span>
//                 </div>

//                 {/* TITLE */}
//                 <h3>
//                   {
//                     item.requirement_title_reference
//                   }
//                 </h3>

//                 <p>
//                   {
//                     item.requirement_purpose
//                   }
//                 </p>

//                 {/* CHIPS */}
//                 <div className="card-tags">
//                   <span
//                     className={`priority-chip ${priorityClass(
//                       item.priority
//                     )}`}
//                   >
//                     {
//                       item.priority
//                     }
//                   </span>

//                   <span className="moscow-chip">
//                     {
//                       item.requirement_moscow
//                     }
//                   </span>
//                 </div>

//                 {/* FOOTER */}
//                 <div className="card-footer">
//                   <span>
//                     {
//                       item
//                         .features
//                         ?.length
//                     }{" "}
//                     Features
//                   </span>

//                   <button
//                     className="view-btn"
//                     onClick={() =>
//                       setSelectedRequirement(
//                         item
//                       )
//                     }
//                   >
//                     <FiEye />
//                     View Details
//                   </button>
//                 </div>
//               </motion.div>
//             )
//           )}
//         </div>
//       )}

//       {/* VIEW DETAILS */}
//       <ViewDetails
//         requirement={
//           selectedRequirement
//         }
//         setRequirement={
//           setSelectedRequirement
//         }
//         refreshRequirements={
//           fetchRequirements
//         }
//       />
//     </div>
//   );
// };

// export default Dashboard;